import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import { Info } from "lucide-react";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (!res.ok) setError(res.error);
    else navigate("/dashboard");
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid md:grid-cols-2" style={{ background: "var(--bq-bg)" }}>
      <div className="hidden md:flex bq-dark text-white p-12 flex-col justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="register-brand">
          <Logo variant="word" height={28} linkTo={null} invert testId="register-brand-logo" />
        </Link>
        <div>
          <div className="overline text-white/60 mb-4">Welcome</div>
          <h1 className="font-display text-5xl font-extrabold leading-tight">
            Start your<br />
            project file<br />
            <span className="text-[var(--bq-orange)]">in minutes.</span>
          </h1>
        </div>
        <div className="text-xs text-white/50">No credit card. POPIA compliant.</div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="overline mb-2">Create customer account</div>
          <h2 className="font-display text-3xl font-extrabold">Get started in 30 seconds</h2>
          <p className="text-sm text-[var(--bq-text-muted)] mt-2">
            Already have an account?{" "}
            <Link to="/login" className="text-[var(--bq-orange)] font-semibold" data-testid="link-login">
              Sign in
            </Link>
            .
          </p>

          <div className="mt-5 flex items-start gap-2 bg-[var(--bq-bg)] border border-[var(--bq-border)] rounded-md p-3 text-xs text-[var(--bq-text-muted)]" data-testid="role-notice">
            <Info size={14} className="shrink-0 mt-0.5" />
            <span>
              This signup creates a <b>customer</b> account. Staff accounts (surveyor, estimator, project manager) are issued by an administrator.
            </span>
          </div>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="register-form">
            <div>
              <label className="overline">Full name</label>
              <input
                value={form.name} onChange={set("name")} required minLength={1} maxLength={120}
                data-testid="register-name"
                className="mt-1 w-full px-3 py-2.5 bg-white border border-[var(--bq-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--bq-orange)]"
              />
            </div>
            <div>
              <label className="overline">Email</label>
              <input
                type="email" value={form.email} onChange={set("email")} required
                data-testid="register-email"
                className="mt-1 w-full px-3 py-2.5 bg-white border border-[var(--bq-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--bq-orange)]"
              />
            </div>
            <div>
              <label className="overline">Phone (optional)</label>
              <input
                value={form.phone} onChange={set("phone")} maxLength={40}
                data-testid="register-phone"
                className="mt-1 w-full px-3 py-2.5 bg-white border border-[var(--bq-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--bq-orange)]"
              />
            </div>
            <div>
              <label className="overline">Password (min 8 characters)</label>
              <input
                type="password" value={form.password} onChange={set("password")} required minLength={8}
                data-testid="register-password"
                className="mt-1 w-full px-3 py-2.5 bg-white border border-[var(--bq-border)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--bq-orange)]"
              />
            </div>
            {error && (
              <div className="text-sm text-[var(--bq-red)] bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="register-error">
                {error}
              </div>
            )}
            <button
              type="submit" disabled={loading} data-testid="register-submit"
              className="w-full bq-orange-btn py-2.5 rounded-md font-semibold disabled:opacity-60"
            >
              {loading ? "Creating account…" : "Create customer account"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
